import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, CreatorRoute, AdminRoute } from "@/components/ProtectedRoute";
import { AdminLayout } from "@/components/AdminLayout";
import { SubscriberLayout } from "@/components/SubscriberLayout";
import { CreatorLayout } from "@/components/CreatorLayout";

import Splash from "@/pages/Splash";
import Welcome from "@/pages/Welcome";
import Register from "@/pages/Register";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Setup from "@/pages/Setup";
import Legal from "@/pages/Legal";
import Invite from "@/pages/Invite";
import SandboxPay from "@/pages/SandboxPay";
import SandboxConnect from "@/pages/SandboxConnect";
import AdminFinance from "@/pages/admin/Finance";
import AdminOwnerReview from "@/pages/admin/OwnerReview";
import AdminDemoAccess from "@/pages/admin/DemoAccess";
import Discover from "@/pages/sub/Discover";
import Library from "@/pages/sub/Library";
import Following from "@/pages/sub/Following";
import Messages from "@/pages/sub/Messages";
import Promotion from "@/pages/creator/Promotion";
import Creators from "@/pages/sub/Creators";
import CreatorProfilePage from "@/pages/sub/CreatorProfile";
import CheckoutReturn from "@/pages/sub/CheckoutReturn";
import CreatorStudioProfile from "@/pages/creator/Profile";
import AdminOverview from "@/pages/admin/Overview";
import AdminCreators from "@/pages/admin/Creators";
import AdminPricing from "@/pages/admin/Pricing";
import AdminRevenue from "@/pages/admin/Revenue";
import AdminTransactions from "@/pages/admin/Transactions";
import AdminProvider from "@/pages/admin/Provider";
import LiveStudio from "@/pages/creator/LiveStudio";
import LiveWatch from "@/pages/sub/LiveWatch";
import AdminIntegrations from "@/pages/admin/Integrations";
import AdminLiveMonitor from "@/pages/admin/LiveMonitor";
import AdminActivity from "@/pages/admin/Activity";
import CreatorAccount from "@/pages/creator/Account";

import Videos from "@/pages/sub/Videos";
import Photos from "@/pages/sub/Photos";
import Live from "@/pages/sub/Live";
import Membership from "@/pages/sub/Membership";
import Checkout from "@/pages/sub/Checkout";
import Profile from "@/pages/sub/Profile";
import Settings from "@/pages/sub/Settings";

import Dashboard from "@/pages/creator/Dashboard";
import Content from "@/pages/creator/Content";
import Subscribers from "@/pages/creator/Subscribers";
import LiveControl from "@/pages/creator/LiveControl";
import MembershipSettings from "@/pages/creator/MembershipSettings";
import Billing from "@/pages/creator/Billing";
import CreatorFollowers from "@/pages/creator/Followers";
import CreatorPayouts from "@/pages/creator/Payouts";
import CreatorNotifications from "@/pages/creator/Notifications";
import LiveChatHub from "@/pages/creator/LiveChatHub";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Splash />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/register" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/legal/:doc" element={<Legal />} />
            <Route path="/invite" element={<Invite />} />
            <Route path="/pay/sandbox/connect/:id" element={<ProtectedRoute><SandboxConnect /></ProtectedRoute>} />
            <Route path="/pay/sandbox/:id" element={<ProtectedRoute><SandboxPay /></ProtectedRoute>} />

            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<AdminOverview />} />
              <Route path="creators" element={<AdminCreators />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="revenue" element={<AdminRevenue />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="owner-review" element={<AdminOwnerReview />} />
              <Route path="demo-access" element={<AdminDemoAccess />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="provider" element={<AdminProvider />} />
              <Route path="integrations" element={<AdminIntegrations />} />
              <Route path="live" element={<AdminLiveMonitor />} />
              <Route path="activity" element={<AdminActivity />} />
            </Route>

            {/* Subscriber app (protected) */}
            <Route path="/app/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <SubscriberLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/app/discover" replace />} />
              <Route path="home" element={<Navigate to="/app/discover" replace />} />
              <Route path="library" element={<Library />} />
              <Route path="following" element={<Following />} />
              <Route path="messages" element={<Messages />} />
              <Route path="creators" element={<Creators />} />
              <Route path="c/:handle" element={<CreatorProfilePage />} />
              <Route path="checkout/return" element={<CheckoutReturn />} />
              <Route path="videos" element={<Videos />} />
              <Route path="photos" element={<Photos />} />
              <Route path="live" element={<Live />} />
              <Route path="live/:sessionId" element={<LiveWatch />} />
              <Route path="membership" element={<Membership />} />
              <Route path="checkout" element={<Checkout />} />
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* Creator administration (protected + role gate) */}
            <Route
              path="/creator"
              element={
                <CreatorRoute>
                  <CreatorLayout />
                </CreatorRoute>
              }
            >
              <Route index element={<Navigate to="/creator/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile" element={<CreatorStudioProfile />} />
              <Route path="content" element={<Content />} />
              <Route path="photos" element={<Content kind="album" />} />
              <Route path="videos" element={<Content kind="video" />} />
              <Route path="posts" element={<Content kind="post" />} />
              <Route path="live-chat" element={<LiveChatHub />} />
              <Route path="followers" element={<CreatorFollowers />} />
              <Route path="payouts" element={<CreatorPayouts />} />
              <Route path="notifications" element={<CreatorNotifications />} />
              <Route path="subscribers" element={<Subscribers />} />
              <Route path="live" element={<LiveControl />} />
              <Route path="live/:sessionId/studio" element={<LiveStudio />} />
              <Route path="membership" element={<MembershipSettings />} />
              <Route path="billing" element={<Billing />} />
              <Route path="account" element={<CreatorAccount />} />
              <Route path="promotion" element={<Promotion />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
